import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AtsMessageTemplatesListDocument,
  AtsNewMessageTemplateDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface NewMessageTemplateFormValues {
  name: string
}

interface Props {
  onClose: () => void
}

export const NewMessageTemplateModal = ({ onClose }: Props) => {
  const {
    company: { id: companyId },
    access: { id: currentAccessId },
  } = useCurrentSession()

  const [createAtsMessageTemplate, { loading: creatingMessageTemplate }] = useMutation(
    AtsNewMessageTemplateDocument,
    {
      extractorFn: (data) => data.atsMutations.createAtsMessageTemplate,
      refetchQueries: [AtsMessageTemplatesListDocument],
    }
  )
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const form = useForm<NewMessageTemplateFormValues>({
    defaultValues: {
      name: '',
    },
    validate(data) {
      const errors: FormErrors<NewMessageTemplateFormValues> = {}

      if (form.dirty && (!data.name || data.name === '')) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await createAtsMessageTemplate({
        variables: {
          authorId: currentAccessId,
          companyId: companyId,
          name: values.name,
          visibility: 'personal',
        },
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.manage_message_templates.create_modal.success_created_message'),
        })

        const { messageTemplate } = handled.data

        if (messageTemplate) {
          navigate(
            navigation.settings.ats.templates.messageTemplatesId.fullPath({
              messageTemplateId: messageTemplate.id,
            })
          )
        }
      }

      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('ats.manage_message_templates.create_modal.title')}
      description={i18n.t('ats.manage_message_templates.create_modal.description')}
      illustration="document-edit"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button disabled={!form.dirty || creatingMessageTemplate || !form.isValid} submit>
          {i18n.t('ats.manage_message_templates.create_modal.create_button')}
        </Button>
      }
    >
      <TextField
        label={i18n.t('ats.manage_message_templates.create_modal.name_label')}
        value={form.values.name}
        onChange={(name) => form.setFieldValue('name', name)}
        error={form.errors.name}
      />
    </Modal>
  )
}
